import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Card, Link, Snackbar, Tooltip, Typography } from '@mui/material';
import { ContentCopy, Link as LinkIcon } from '@mui/icons-material';
import moment from 'moment-timezone';
import colorsStyles from '../colors.styles';
import { isEditableVideoTemplate } from './Template.model';
import { getTemplateAvailability } from './Templates.service';

const STATUS_PILLS = {
  live: {
    label: 'LIVE',
    color: '#1F8A4C',
    background: 'rgba(31, 138, 76, 0.12)',
    dot: true,
  },
  upcoming: {
    label: 'UPCOMING',
    color: '#0053E2',
    background: 'rgba(0, 83, 226, 0.12)',
    dot: false,
  },
  expired: {
    label: 'ENDED',
    color: '#66788A',
    background: 'rgba(102, 120, 138, 0.14)',
    dot: false,
  },
};

const TYPE_LABELS = {
  pw: 'Pre-written',
  pl: 'Product link',
  fitb: 'Fill-in-the-blank',
};

function templateTypeLabel(template) {
  if (isEditableVideoTemplate(template)) return 'Editable videos';
  return TYPE_LABELS[template.category] || 'Pre-written';
}

function templateDateLabel(template) {
  const startDate = template.start_date
    ? moment(template.start_date).tz('America/Chicago').format('MM/DD/YY')
    : null;
  const endDate = template.end_date
    ? moment(template.end_date).tz('America/Chicago').format('MM/DD/YY')
    : '∞';

  if (!startDate && !template.end_date) return '∞';
  return `${startDate || '∞'} - ${endDate}`;
}

export default function TemplateItem({ template, onClick }) {
  const {
    id,
    type,
    source,
    picture,
    message,
    link,
    category,
    topic,
    subtopic,
    title,
    description,
  } = template;
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const replacePlaceHolders = (templateCategory, templateMessage) => {
    if (templateCategory !== 'fitb') return templateMessage;
    return templateMessage.split(/(\{[^}]+\})/g).map((part, index) => {
      if (/\{[^}]+\}/.test(part)) {
        // eslint-disable-next-line react/no-array-index-key
        return <BlankSpace key={index} placeholder={part.slice(1, -1)} />;
      }
      return part;
    });
  };

  const copyToClipboard = (e) => {
    navigator.clipboard.writeText(id);
    setOpenSnackbar(true);
    e.stopPropagation();
  };

  const thumbnailSrc = type === 'video' ? picture : source;

  const styles = {
    card: {
      padding: '16px',
      borderRadius: '18px',
      cursor: 'pointer',
    },
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '8px',
    },
    thumbnail: {
      width: '134px', // from Figma
      height: '134px',
      objectFit: 'cover',
      borderRadius: '8px',
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      maxWidth: thumbnailSrc ? 'calc(100% - 150px)' : '100%',
    },
    text: {
      display: '-webkit-box',
      WebkitLineClamp: link !== null ? 2 : 7,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'pre-wrap',
    },
    linkContainer: { display: 'flex', alignItems: 'center', gap: '4px' },
    link: {
      color: colorsStyles.base.walmart_blue,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      display: 'inline-block',
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    linkIcon: {
      transform: 'rotate(-45deg)',
      color: colorsStyles.text.gray,
    },
    idButton: { textTransform: 'none' },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
    },
    pills: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: 0,
    },
    statusPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      borderRadius: '6px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      lineHeight: '16px',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
    },
    typePill: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '6px',
      backgroundColor: '#F2F4F7',
      color: '#3E5166',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      padding: '2px 8px',
      whiteSpace: 'nowrap',
    },
    idGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      minWidth: 0,
      maxWidth: '46%',
    },
    idText: {
      fontSize: '12px',
      color: '#8B9BB0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    copyIcon: {
      fontSize: 16,
      color: '#8B9BB0',
      cursor: 'pointer',
      flexShrink: 0,
    },
    productName: {
      fontSize: '14px',
      fontWeight: 700,
      color: '#1B2838',
      marginBottom: '4px',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
    },
    footerTopic: {
      fontSize: '13px',
      color: '#66788A',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    footerDate: {
      fontSize: '13px',
      color: '#66788A',
      whiteSpace: 'nowrap',
    },
  };

  const availability = getTemplateAvailability(template);
  const statusPill = STATUS_PILLS[availability] || STATUS_PILLS.live;
  const topicLabel = [topic?.name, subtopic?.name].filter(Boolean).join(', ');

  return (
    <>
      <Card sx={styles.card} onClick={onClick}>
        <Box sx={styles.container}>
          <Box sx={styles.header}>
            <Box sx={styles.pills}>
              <Box
                sx={{
                  ...styles.statusPill,
                  color: statusPill.color,
                  backgroundColor: statusPill.background,
                }}
              >
                {statusPill.dot && (
                  <Box
                    sx={{
                      ...styles.statusDot,
                      backgroundColor: statusPill.color,
                    }}
                  />
                )}
                {statusPill.label}
              </Box>
              <Box sx={styles.typePill}>{templateTypeLabel(template)}</Box>
            </Box>
            <Box sx={styles.idGroup}>
              <Typography sx={styles.idText}>{id}</Typography>
              <Tooltip title="Click to copy the ID" placement="top" arrow>
                <ContentCopy sx={styles.copyIcon} onClick={copyToClipboard} />
              </Tooltip>
            </Box>
          </Box>
          <Box display="flex" gap={2}>
            {thumbnailSrc && (
              <img
                style={styles.thumbnail}
                alt={`template ${id}`}
                src={thumbnailSrc}
              />
            )}
            <Box sx={styles.contentContainer}>
              {category !== 'pl' ? (
                <Typography variant="body2" component="span" sx={styles.text}>
                  {replacePlaceHolders(category, message)}
                </Typography>
              ) : (
                <>
                  {title && (
                    <Typography sx={styles.productName}>{title}</Typography>
                  )}
                  {description && (
                    <Typography variant="body2" sx={styles.text}>
                      {description}
                    </Typography>
                  )}
                  <Typography variant="body2" component="span" sx={styles.text}>
                    {replacePlaceHolders(category, message)}
                  </Typography>
                  {link && (
                    <Box sx={styles.linkContainer}>
                      <LinkIcon sx={styles.linkIcon} />
                      <Link href={link} underline="hover" sx={styles.link}>
                        {link}
                      </Link>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
          <Box sx={styles.footer}>
            <Typography sx={styles.footerTopic}>{topicLabel}</Typography>
            <Typography sx={styles.footerDate}>
              {templateDateLabel(template)}
            </Typography>
          </Box>
        </Box>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        message="ID copied to clipboard"
        onClose={() => setOpenSnackbar(false)}
      />
    </>
  );
}

function BlankSpace({ placeholder }) {
  return (
    <Box
      sx={{
        display: 'inline-block',
        borderRadius: '5px',
        paddingX: '5px',
        backgroundColor: colorsStyles.base.very_light_gray,
      }}
    >
      <Typography variant="body2" color={colorsStyles.text.gray}>
        {placeholder}
      </Typography>
    </Box>
  );
}

TemplateItem.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    source: PropTypes.string,
    picture: PropTypes.string,
    message: PropTypes.string,
    link: PropTypes.string,
    category: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    topic: PropTypes.shape({
      name: PropTypes.string,
    }),
    subtopic: PropTypes.shape({
      name: PropTypes.string,
    }),
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

BlankSpace.propTypes = {
  placeholder: PropTypes.string,
};

BlankSpace.defaultProps = {
  placeholder: 'BLANK',
};
